/**
 * Resilience State Store (Zustand)
 *
 * ISSUE_045: 연결 복원력 시스템 상태 관리
 *
 * @module core/resilience/store
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type {
  ResilienceState,
  ResilienceActions,
  ConnectionState,
  HealthStatus,
  HealthCheckResult,
} from './types';
import { DEFAULT_CIRCUIT_CONFIG } from './types';

type ResilienceStore = ResilienceState & ResilienceActions;

/**
 * 초기 상태
 */
const initialState: ResilienceState = {
  // 연결 상태 (Supabase 클라이언트가 있으면 기본적으로 연결됨)
  connectionState: 'connected',
  reconnectAttempts: 0,
  lastConnectedAt: null,

  // 헬스 상태
  healthStatus: 'healthy',
  lastHealthCheck: null,

  // Circuit Breaker
  circuitState: 'closed',
  circuitFailures: 0,
  circuitOpenedAt: null,

  // 통계
  totalReconnects: 0,
  totalTokenRefreshes: 0,
  lastError: null,
};

/**
 * Resilience Store
 */
export const useResilienceStore = create<ResilienceStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      // =======================================================================
      // CONNECTION
      // =======================================================================

      setConnectionState: (state: ConnectionState) => {
        const prev = get().connectionState;

        set({ connectionState: state }, false, 'setConnectionState');

        // 연결 성공 시 타임스탬프 기록
        if (state === 'connected' && prev !== 'connected') {
          set({ lastConnectedAt: Date.now() }, false, 'setLastConnectedAt');
        }
      },

      incrementReconnectAttempts: () => {
        set(
          (s) => ({ reconnectAttempts: s.reconnectAttempts + 1 }),
          false,
          'incrementReconnectAttempts'
        );
      },

      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 }, false, 'resetReconnectAttempts');
      },

      // =======================================================================
      // HEALTH
      // =======================================================================

      setHealthStatus: (status: HealthStatus) => {
        set({ healthStatus: status }, false, 'setHealthStatus');
      },

      setLastHealthCheck: (result: HealthCheckResult) => {
        set({ lastHealthCheck: result }, false, 'setLastHealthCheck');
      },

      // =======================================================================
      // CIRCUIT BREAKER
      // =======================================================================

      recordFailure: () => {
        const { circuitFailures, circuitState } = get();
        const newCount = circuitFailures + 1;

        set({ circuitFailures: newCount }, false, 'recordFailure');

        // 임계값 도달 시 서킷 열기
        if (circuitState === 'closed' && newCount >= DEFAULT_CIRCUIT_CONFIG.failureThreshold) {
          get().tripCircuit();
        }
      },

      recordSuccess: () => {
        const { circuitState } = get();

        if (circuitState === 'half-open') {
          // HALF_OPEN에서 성공하면 CLOSED로
          get().resetCircuit();
        } else if (circuitState === 'closed') {
          // CLOSED에서 성공하면 실패 카운터 리셋
          set({ circuitFailures: 0 }, false, 'recordSuccess');
        }
      },

      tripCircuit: () => {
        set(
          {
            circuitState: 'open',
            circuitOpenedAt: Date.now(),
          },
          false,
          'tripCircuit'
        );
      },

      resetCircuit: () => {
        set(
          {
            circuitState: 'closed',
            circuitFailures: 0,
            circuitOpenedAt: null,
          },
          false,
          'resetCircuit'
        );
      },

      // =======================================================================
      // ERROR
      // =======================================================================

      setLastError: (error: string | null) => {
        set({ lastError: error }, false, 'setLastError');
      },

      // =======================================================================
      // STATISTICS
      // =======================================================================

      incrementReconnects: () => {
        set(
          (s) => ({ totalReconnects: s.totalReconnects + 1 }),
          false,
          'incrementReconnects'
        );
      },

      incrementTokenRefreshes: () => {
        set(
          (s) => ({ totalTokenRefreshes: s.totalTokenRefreshes + 1 }),
          false,
          'incrementTokenRefreshes'
        );
      },
    })),
    { name: 'resilience-store' }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * 연결 상태 선택자
 */
export const selectConnectionState = (state: ResilienceStore) => state.connectionState;

/**
 * 연결됨 여부
 */
export const selectIsConnected = (state: ResilienceStore) =>
  state.connectionState === 'connected';

/**
 * 재연결 중 여부
 */
export const selectIsReconnecting = (state: ResilienceStore) =>
  state.connectionState === 'reconnecting';

/**
 * 헬스 상태 선택자
 */
export const selectHealthStatus = (state: ResilienceStore) => state.healthStatus;

/**
 * 정상 상태 여부
 */
export const selectIsHealthy = (state: ResilienceStore) =>
  state.healthStatus === 'healthy';

/**
 * 서킷 상태 선택자
 */
export const selectCircuitState = (state: ResilienceStore) => state.circuitState;

/**
 * 서킷 열림 여부
 */
export const selectIsCircuitOpen = (state: ResilienceStore) =>
  state.circuitState === 'open';

/**
 * 통계 선택자
 */
export const selectStats = (state: ResilienceStore) => ({
  totalReconnects: state.totalReconnects,
  totalTokenRefreshes: state.totalTokenRefreshes,
  reconnectAttempts: state.reconnectAttempts,
  circuitFailures: state.circuitFailures,
});

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * 상태 변경 구독 (디버깅용)
 */
export function subscribeToResilienceChanges(
  selector: (state: ResilienceStore) => unknown,
  callback: (value: unknown, prevValue: unknown) => void
) {
  return useResilienceStore.subscribe(selector, callback);
}
